import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { Carousel, OpenInNewTab, Skeleton, TwitterBlueVerified, Tooltip } from '@netbasequid/core'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import { InsightContentSettingType } from 'components/insightContent/types'
import Divider from 'components/visualization/MediaModal/Divider'
import { useInsightContentData } from 'hooks/useInsightModalState'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import { ServiceResultWith } from 'schemas/ServiceResultSchema'
import { Status } from 'features/types'
import { Post } from 'schemas/PostSchema'
import { DocumentRetrievalResult } from 'schemas/DocumentRetrievalResultSchema'
import { isRepost } from 'utils/postTypeUtil'
import Link from 'components/visualization/MediaModal/Link'
import Date from 'components/visualization/MediaModal/Date'
import DomainLink from 'components/visualization/MediaModal/DomainLink'
import TwitterActions from 'components/visualization/MediaModal/TwitterActions'
import Domain from 'enums/Domain'
import ActionButton from 'components/visualization/MediaModal/ActionButton'
import { getDomainName } from 'utils/sourceUtil'
import { setAutoId, AutomationId } from 'utils/automationUtil'
import RunSearchEffect from './RunSearchEffect'
import AuthorAvatar from './AuthorAvatar'
import RetweetedAuthor from './RetweetedAuthor'
import { retrieveDocumentMetadata } from './utils'
import DividerWrapper from './DividerWrapper'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const Viewer = styled.div`
  height: 460px;
`

const StyledDivider = styled(Divider)`
  margin-top: 16px;
  margin-bottom: 16px;
`

const Information = styled.div`
  margin-bottom: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0px 8px;
  font-weight: ${sys.fontWeight.regular};
  font-size: ${sys.typescale[14].size};
  line-height: ${sys.typescale[14].lineHeight};
`

const AuthorNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0 4px;
`

const AuthorName = styled.div.attrs(() => setAutoId(AutomationId.photoConcept.authorName))<{
  mode: Theme
}>`
  color: ${({ mode }) => sys[mode].interaction.primary.default};
`

const AuthorHandle = styled.div.attrs(() => setAutoId(AutomationId.photoConcept.authorHandle))<{
  mode: Theme
}>`
  color: ${({ mode }) => sys[mode].text.placeholder};
`

const StyledDate = styled(Date)`
  font-size: ${sys.typescale[14].size};
  line-height: ${sys.typescale[14].lineHeight};
`

interface ImageProps {
  imageUrl?: string
  originUrl?: string
  detailSetting: InsightContentSettingType
  post: Post
}

const PhotoDetail = ({ imageUrl, originUrl, detailSetting, post }: ImageProps) => {
  const theme = useDisplayTheme() as Theme
  const { t } = useTranslation()

  const insightContent = useInsightContentData<{
    status: Status
    data: ServiceResultWith<DocumentRetrievalResult>[]
  }>(InsightContentType.PHOTO_CONCEPT_MEDIA)
  const data = retrieveDocumentMetadata(insightContent?.data)

  const {
    postType,
    dataStream,
    authorHandle,
    authorName,
    authorUrl,
    authorVerified,
    originalAuthorHandle,
    originalAuthorName,
    originalAuthorUrl,
    originalAuthorVerified,
    datetime,
    domain
  } = data
  const isRetweet = isRepost(postType, dataStream)

  const resolvedAuthorUrl = isRetweet ? originalAuthorUrl : authorUrl
  const resolvedAuthorName = isRetweet ? originalAuthorName : authorName
  const resolvedAuthorHandle = isRetweet ? originalAuthorHandle : authorHandle
  const resolvedAuthorVerified = isRetweet ? originalAuthorVerified : authorVerified

  return (
    <RunSearchEffect detailSetting={detailSetting} post={post}>
      <Container>
        <Viewer>
          <Carousel
            mode={theme}
            showPagination={false}
            slides={[
              {
                type: 'Image',
                url: imageUrl,
                fallback: {
                  originUrl,
                  infoUrl: 'https://help.quid.com/articles/8240236#h_24b221c46b'
                }
              }
            ]}
          />
        </Viewer>
        <StyledDivider width="100%" />
        <Information>
          {insightContent?.status === 'loading' && (
            <Skeleton
              mode={theme}
              variation="rectangle"
              animation="wave"
              width="100%"
              height="32px"
            />
          )}
          {insightContent?.status === 'succeeded' && (
            <>
              <DividerWrapper>
                <>
                  <AuthorAvatar isRetweet={isRetweet} data={data} />
                  {(resolvedAuthorName || resolvedAuthorVerified) && (
                    <AuthorNameContainer>
                      {resolvedAuthorName && (
                        <Link href={resolvedAuthorUrl}>
                          <AuthorName mode={theme}>{resolvedAuthorName}</AuthorName>
                        </Link>
                      )}
                      {resolvedAuthorVerified && (
                        <Tooltip mode={theme} content={t('verifiedAccount')}>
                          <TwitterBlueVerified mode={theme} />
                        </Tooltip>
                      )}
                    </AuthorNameContainer>
                  )}
                </>
              </DividerWrapper>
              {resolvedAuthorHandle && (
                <DividerWrapper>
                  <AuthorHandle mode={theme}>{resolvedAuthorHandle}</AuthorHandle>
                </DividerWrapper>
              )}
              {isRetweet && (
                <DividerWrapper>
                  <RetweetedAuthor dataStream={dataStream} name={authorName} url={authorUrl} />
                </DividerWrapper>
              )}
              <DividerWrapper>
                <StyledDate timestamp={Number(datetime)} postUrl={originUrl} />
              </DividerWrapper>
              <DividerWrapper>
                <DomainLink domain={domain} dataStream={dataStream} />
              </DividerWrapper>
              {domain === Domain.TWITTER && (
                <DividerWrapper>
                  <TwitterActions postId={post.postID} />
                </DividerWrapper>
              )}
              <ActionButton
                url={originUrl}
                IconComponent={OpenInNewTab}
                tooltipContent={
                  data.domain && `${t('viewOn', { source: getDomainName(data.domain) })}`
                }
              />
            </>
          )}
        </Information>
      </Container>
    </RunSearchEffect>
  )
}

export default PhotoDetail
